import React, { useRef, useState, useLayoutEffect } from 'react';

/* Переключатель в стиле iOS / «жидкое стекло»: скользящий блик между сегментами.
   Работает и кликом, и перетаскиванием (палец/мышь) — блик едет за пальцем,
   на отпускании прилипает к ближайшему сегменту.
   options: [{ key, label, count? }], value, onChange, className ('dark' для тёмного фона). */
export default function SegToggle({ options, value, onChange, className = '' }) {
  const n = options.length;
  const idx = Math.max(0, options.findIndex(o => o.key === value));
  const trackRef = useRef(null);
  const btnRefs = useRef({});
  const st = useRef({ down: false, startX: 0, moved: false });
  const [dragLeft, setDragLeft] = useState(null); // px-смещение блика во время перетаскивания
  // Сегменты не всегда равной ширины: «1fr» не может сжать колонку уже её
  // содержимого, и на узком экране блик наезжал на соседний пункт. Поэтому
  // меряем реальные кнопки и ставим блик ровно по активной.
  const [seg, setSeg] = useState(null); // { left, width }

  useLayoutEffect(() => {
    const measure = () => {
      const btn = btnRefs.current[options[idx]?.key];
      if (!trackRef.current || !btn) return;
      // offsetLeft считается от того же offsetParent, что и позиция блика,
      // поэтому рамку и паддинг дорожки вычитать не нужно
      setSeg({ left: btn.offsetLeft, width: btn.offsetWidth });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && trackRef.current) ro.observe(trackRef.current);
    window.addEventListener('resize', measure);
    return () => { ro?.disconnect(); window.removeEventListener('resize', measure); };
  }, [idx, options.length]); // eslint-disable-line

  const segW = () => {
    const el = trackRef.current;
    return el ? (el.clientWidth - 8) / n : 0; // 4px паддинга с каждой стороны
  };

  const onDown = (e) => {
    // НЕ захватываем указатель сразу — иначе click не доходит до кнопки и переключение ломается.
    st.current = { down: true, startX: e.clientX, moved: false };
  };

  const onMove = (e) => {
    if (!st.current.down) return;
    if (!st.current.moved && Math.abs(e.clientX - st.current.startX) < 4) return;
    if (!st.current.moved) {
      // Перетаскивание реально началось — теперь можно захватить указатель.
      st.current.moved = true;
      trackRef.current?.setPointerCapture?.(e.pointerId);
    }
    const rect = trackRef.current.getBoundingClientRect();
    const w = segW();
    let left = e.clientX - rect.left - 4 - w / 2; // центр блика под пальцем
    left = Math.max(0, Math.min((n - 1) * w, left));
    setDragLeft(left);
  };

  const onUp = (e) => {
    const wasDrag = st.current.moved;
    st.current.down = false;
    st.current.moved = false;
    if (!wasDrag) { setDragLeft(null); return; } // обычный клик обработает кнопка
    const rect = trackRef.current.getBoundingClientRect();
    const w = segW();
    let i = Math.round((e.clientX - rect.left - 4 - w / 2) / w);
    i = Math.max(0, Math.min(n - 1, i));
    setDragLeft(null);
    if (options[i] && options[i].key !== value) onChange(options[i].key);
  };

  const thumbStyle = dragLeft != null
    ? { width: seg ? seg.width : `calc((100% - 8px) / ${n})`, transform: `translateX(${dragLeft}px)`, transition: 'none' }
    : seg
      ? { left: 0, width: seg.width, transform: `translateX(${seg.left}px)` }
      : { width: `calc((100% - 8px) / ${n})`, transform: `translateX(${idx * 100}%)` };

  return (
    <div
      className={`seg ${className}${dragLeft != null ? ' dragging' : ''}`}
      role="tablist"
      ref={trackRef}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
    >
      <div className="seg-thumb" style={thumbStyle} />
      {options.map(o => (
        <button
          key={o.key}
          type="button"
          role="tab"
          aria-selected={o.key === value}
          ref={el => { btnRefs.current[o.key] = el; }}
          className={`seg-btn${o.key === value ? ' active' : ''}`}
          onClick={() => onChange(o.key)}
        >
          {o.label}
          {o.count != null && o.count !== '' && <span className="seg-cnt">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}
